import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listExamAttempts, runExam, type ExamAttempt } from '../api/client';
import { examSections, savedStrategies } from '../research/content';

/** The exam's bar, as the server grades it (docs/exam/README.md). */
const GOAL_MONTHS = 80;
const MINIMUM_MONTHS = 50;
const MAX_FALL = 15;

function num(x: string | number): number {
  return typeof x === 'number' ? x : Number(x);
}

function PassBadge({ label, passed }: { label: string; passed: boolean | null }) {
  if (passed === null) return <span className="badge badge--gray">{label}: n/a</span>;
  return <span className={`badge ${passed ? 'badge--green' : 'badge--red'}`}>{`${label}: ${passed ? 'pass' : 'fail'}`}</span>;
}

/** A 0–100% bar with marks at the minimum and the goal. */
function MonthsBar({ pct }: { pct: number }) {
  const cls = pct >= GOAL_MONTHS ? 'exam-bar-fill--green' : pct >= MINIMUM_MONTHS ? 'exam-bar-fill--yellow' : 'exam-bar-fill--red';
  return (
    <div className="exam-bar" aria-label={`${pct.toFixed(0)}% of months profitable`}>
      <div className={`exam-bar-fill ${cls}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
      <span className="exam-bar-mark" style={{ left: `${MINIMUM_MONTHS}%` }} title="Minimum: 50%" />
      <span className="exam-bar-mark exam-bar-mark--goal" style={{ left: `${GOAL_MONTHS}%` }} title="Goal: 80%" />
    </div>
  );
}

/** Worst fall against the 15% limit, on a 0–30% scale. */
function FallBar({ pct }: { pct: number }) {
  const scale = MAX_FALL * 2;
  return (
    <div className="exam-bar" aria-label={`worst fall ${pct.toFixed(1)}%`}>
      <div
        className={`exam-bar-fill ${pct <= MAX_FALL ? 'exam-bar-fill--green' : 'exam-bar-fill--red'}`}
        style={{ width: `${Math.min(100, (pct / scale) * 100)}%` }}
      />
      <span className="exam-bar-mark exam-bar-mark--goal" style={{ left: '50%' }} title="Limit: 15%" />
    </div>
  );
}

function AttemptCard({ a, repeat }: { a: ExamAttempt; repeat: boolean }) {
  const months = num(a.profitable_months_pct);
  const fall = num(a.max_drawdown_pct);
  const ret = num(a.total_return_pct);
  return (
    <div className="card exam-attempt">
      <div className="exam-attempt-head">
        <div>
          <strong>{a.strategy_name ?? <span className="cell-mono">{a.spec_hash.slice(7, 19)}</span>}</strong>
          <div className="section-description exam-attempt-when">
            {new Date(a.attempted_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
            {' · exam period from '}
            {a.holdout_start.slice(0, 10)}
          </div>
        </div>
        <div className="exam-attempt-badges">
          <PassBadge label="Goal" passed={a.passed} />
          <PassBadge label="Minimum" passed={a.minimum_passed} />
          {repeat && <span className="badge badge--gray">repeat: not a fair test</span>}
        </div>
      </div>
      <div className="exam-attempt-stats">
        <div>
          <div className="exam-stat-label">Months that made money</div>
          <div className="exam-stat-value">
            {months.toFixed(0)}% <span className="exam-stat-sub">({a.profitable_months} of {a.total_months})</span>
          </div>
          <MonthsBar pct={months} />
        </div>
        <div>
          <div className="exam-stat-label">Worst fall</div>
          <div className="exam-stat-value">{fall.toFixed(1)}%</div>
          <FallBar pct={fall} />
        </div>
        <div>
          <div className="exam-stat-label">Return</div>
          <div className={`exam-stat-value ${ret >= 0 ? 'check-pass' : 'check-fail'}`}>
            {ret > 0 ? '+' : ''}
            {ret.toFixed(1)}%
          </div>
        </div>
      </div>
    </div>
  );
}

/** Rules first, then the attempt write-ups newest first, then the technical forex-data note. */
const SECTIONS = (() => {
  const rules = examSections.filter((s) => s.title === 'The exam').map((s) => ({ ...s, title: 'How the exam works' }));
  const forex = examSections.filter((s) => s.title === 'Forex data').map((s) => ({ ...s, title: 'Technical: forex data' }));
  const history = examSections.filter((s) => s.title !== 'The exam' && s.title !== 'Forex data').reverse();
  return [...rules, ...history, ...forex];
})();

export function ExamPage() {
  const queryClient = useQueryClient();
  const attempts = useQuery({ queryKey: ['examAttempts'], queryFn: listExamAttempts });
  const [section, setSection] = useState(0);
  const [selectedStrategyId, setSelectedStrategyId] = useState(savedStrategies[0]?.id ?? '');
  const [examNotice, setExamNotice] = useState<{ type: 'success' | 'error'; message: string; details?: any } | null>(null);

  const list = attempts.data?.data ?? [];
  // Attempts arrive newest first; a strategy seen in an older attempt is a repeat.
  const repeats = new Set(
    list.filter((a, i) => list.slice(i + 1).some((b) => b.spec_hash === a.spec_hash)).map((a) => `${a.spec_hash}-${a.attempted_at}`),
  );
  const minimumPasses = list.filter((a) => a.minimum_passed).length;
  const goalPasses = list.filter((a) => a.passed).length;

  const sitExamMutation = useMutation({
    mutationFn: async () => {
      const found = savedStrategies.find((s) => s.id === selectedStrategyId);
      if (!found) throw new Error('Selected strategy not found');
      return runExam(found.spec);
    },
    onSuccess: (res) => {
      const data = res.data;
      setExamNotice({
        type: 'success',
        message: `Exam completed for "${savedStrategies.find((s) => s.id === selectedStrategyId)?.name}"! Result: ${data.passed ? 'PASSED GOAL (80%)' : data.minimumPassed ? 'PASSED MINIMUM (50%)' : 'Did not meet pass criteria'}. Return: ${data.summary?.totalReturnPct ? (data.summary.totalReturnPct > 0 ? '+' : '') + data.summary.totalReturnPct.toFixed(1) + '%' : 'N/A'}.`,
        details: data,
      });
      queryClient.invalidateQueries({ queryKey: ['examAttempts'] });
    },
    onError: (err: any) => {
      setExamNotice({
        type: 'error',
        message: err?.message || 'Failed to sit the exam. Make sure the backend API is reachable.',
      });
    },
  });

  return (
    <div className="page">
      <h2>The Exam</h2>
      <p className="exam-lead">
        A strategy or fund sits the exam <strong>once</strong>, on months it has never seen (locked from 1 October
        2025). Only the first attempt is a fair test. All attempts are permanently graded and auditable.
      </p>

      {/* Interactive Exam Runner Card */}
      <div className="exam-runner-card">
        <div className="exam-runner-head">
          <div className="exam-runner-title">
            <span>⚡</span>
            <span>Sit Strategy or Fund for Exam (Locked Period)</span>
          </div>
          <span className="badge badge--yellow">Strict Holdout Grade</span>
        </div>
        <p className="section-description" style={{ marginBottom: 16 }}>
          Select a verified candidate from the curriculum or your saved strategies, and test it against the out-of-sample exam period.
        </p>
        <div className="exam-runner-controls">
          <select
            className="input-field"
            style={{ maxWidth: 440, fontWeight: 500 }}
            value={selectedStrategyId}
            onChange={(e) => setSelectedStrategyId(e.target.value)}
          >
            {savedStrategies.map((s) => (
              <option key={s.id} value={s.id}>
                [{s.source}] {s.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn--primary"
            disabled={sitExamMutation.isPending}
            onClick={() => sitExamMutation.mutate()}
          >
            {sitExamMutation.isPending ? 'Grading Exam…' : '📝 Take Exam Now'}
          </button>
        </div>

        {examNotice && (
          <div
            style={{
              marginTop: 16,
              padding: '12px 16px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              backgroundColor: examNotice.type === 'success' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
              color: examNotice.type === 'success' ? '#34d399' : '#f87171',
              border: `1px solid ${examNotice.type === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            }}
          >
            {examNotice.message}
          </div>
        )}
      </div>

      <div className="exam-bar-cards">
        <div className="card exam-target">
          <div className="exam-target-value">80%</div>
          <div className="exam-target-label">of months profitable</div>
          <div className="exam-target-note">The goal</div>
        </div>
        <div className="card exam-target">
          <div className="exam-target-value">50%</div>
          <div className="exam-target-label">of months profitable</div>
          <div className="exam-target-note">The minimum</div>
        </div>
        <div className="card exam-target">
          <div className="exam-target-value">≤ 15%</div>
          <div className="exam-target-label">worst fall</div>
          <div className="exam-target-note">Both grades</div>
        </div>
        <div className="card exam-target">
          <div className="exam-target-value">&gt; T-bills</div>
          <div className="exam-target-label">return beats 3-month Treasury bills</div>
          <div className="exam-target-note">Both grades, from 3 Oct 2026</div>
        </div>
      </div>

      <div className="exam-heading">
        <span>Attempts on this server</span>
        {list.length > 0 && (
          <span className="exam-heading-sub">
            {list.length} attempt{list.length === 1 ? '' : 's'} recorded · {goalPasses} reached goal · {minimumPasses} passed minimum
          </span>
        )}
      </div>
      {attempts.isLoading && <p style={{ color: 'var(--text-muted)' }}>Loading attempts…</p>}
      {attempts.error && <div className="error-message">{(attempts.error as Error).message}</div>}
      {attempts.data && list.length === 0 && (
        <div className="empty-state">
          <div style={{ fontSize: 24, marginBottom: 8 }}>📝</div>
          <strong>No exam attempts recorded yet.</strong>
          <p style={{ marginTop: 4, fontSize: 13, color: 'var(--text-dim)' }}>
            Choose a strategy above and click &quot;Take Exam Now&quot; to test and record the first official attempt.
          </p>
        </div>
      )}
      {list.map((a) => (
        <AttemptCard key={`${a.spec_hash}-${a.attempted_at}`} a={a} repeat={repeats.has(`${a.spec_hash}-${a.attempted_at}`)} />
      ))}


      <h3 className="exam-heading">Write-ups</h3>
      <div className="school-layout">
        <nav className="school-levels" aria-label="Exam write-ups">
          <select
            className="school-level-select"
            value={section}
            onChange={(e) => setSection(Number(e.target.value))}
            aria-label="Choose a write-up"
          >
            {SECTIONS.map((s, i) => (
              <option key={s.title} value={i}>
                {s.title}
              </option>
            ))}
          </select>
          <ul className="school-level-list">
            {SECTIONS.map((s, i) => (
              <li key={s.title}>
                <button
                  type="button"
                  className={`school-level-btn${i === section ? ' school-level-btn--active' : ''}`}
                  onClick={() => setSection(i)}
                >
                  <span className="school-level-name">{s.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <div className="school-main">
          {SECTIONS[section] && (
            <section className="card school-notebook-section">
              <h3>{SECTIONS[section].title}</h3>
              <div className="markdown" dangerouslySetInnerHTML={{ __html: SECTIONS[section].html }} />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
